import { useCallback, useEffect, useRef, useState } from "react";

const TICK_INTERVAL_MS = 150;
const CREEP_INCREMENT = 0.2;

/**
 * Smoothly animates upload progress between discrete milestones.
 *
 * - When a milestone arrives, animates toward it at ~0.5% per tick.
 * - Between milestones, slowly creeps forward (capped 5% past the target).
 * - On completion (isUploading → false), resets immediately to 0.
 *
 * @returns [displayProgress, onMilestone] — the animated percentage and a stable callback to report milestones.
 */
export function useUploadProgress(isUploading: boolean): [number, (pct: number) => void] {
  const [displayProgress, setDisplayProgress] = useState(0);
  const targetRef = useRef(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Stable callback for milestone reports from the upload handler
  const onMilestone = useCallback((pct: number) => {
    targetRef.current = Math.min(pct, 95);
  }, []);

  // Start / stop the animation interval when uploading changes
  useEffect(() => {
    if (isUploading) {
      targetRef.current = 0;
      setDisplayProgress(0);

      intervalRef.current = setInterval(() => {
        setDisplayProgress((prev) => {
          const target = targetRef.current;
          if (prev < target) {
            // Animate toward the milestone
            return Math.min(prev + 0.5, target);
          }
          // Slowly creep forward, but stay 5% past the last milestone
          if (prev >= target + 5 || prev >= 95) {
            return prev;
          }
          return prev + CREEP_INCREMENT;
        });
      }, TICK_INTERVAL_MS);
    } else {
      clearTimer();
      setDisplayProgress(0);
      targetRef.current = 0;
    }

    return clearTimer;
  }, [isUploading]);

  return [Math.round(displayProgress), onMilestone];
}
